import { useState } from 'react';
import { useVelvet } from '@/context/VelvetContext';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/lib/supabase';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Play, Pause, CheckCheck, Package, ClipboardList } from 'lucide-react';

const DESIGNER_ACTIONS = {
  Queued: [{ label: 'Start', next: 'In Production', icon: Play, color: 'bg-foreground text-background' }],
  'In Production': [
    { label: 'Send to QC', next: 'QC', icon: CheckCheck, color: 'bg-[hsl(var(--muted-green-dark))] text-white' },
    { label: 'Block', next: 'Blocked', icon: Pause, color: 'bg-destructive text-destructive-foreground', requiresReason: true },
  ],
  Blocked: [{ label: 'Resume', next: 'In Production', icon: Play, color: 'bg-foreground text-background' }],
  QC: [], // manager acts here
};

export default function DesignerBoard() {
  const { orders, customers, reloadOrders } = useVelvet();
  const { profile, role } = useAuth();
  const [blockFor, setBlockFor] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  // Designer sees their own. Admin/manager see all designer-relevant statuses.
  const relevant = orders.filter((o) => {
    if (['Delivered','Cancelled'].includes(o.status)) return false;
    if (role === 'designer') return o.designer_id === profile?.id && ['Queued','In Production','Blocked','QC'].includes(o.status);
    return ['Queued','In Production','Blocked','QC'].includes(o.status);
  });

  const buckets = {
    Queued: relevant.filter((o) => o.status === 'Queued'),
    'In Production': relevant.filter((o) => o.status === 'In Production'),
    Blocked: relevant.filter((o) => o.status === 'Blocked'),
    QC: relevant.filter((o) => o.status === 'QC'),
  };

  const move = async (orderId, next, reason) => {
    try {
      await apiFetch(`/orders/${orderId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ new_status: next, reason: reason || null }),
      });
      toast.success(`Moved to ${next}`);
      reloadOrders();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-10 max-w-[1200px]" data-testid="designer-board-page">
      <div className="mb-8">
        <div className="velvet-eyebrow mb-2">Production</div>
        <h1 className="velvet-page-title">My production</h1>
        <p className="text-sm text-muted-foreground mt-3">Only your assigned arrangements. Costs are not shown here.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(buckets).map(([status, items]) => (
          <div key={status} data-testid={`bucket-${status.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-baseline justify-between mb-3 px-1">
              <span className="velvet-eyebrow">{status} <span className="text-muted-foreground">· {items.length}</span></span>
            </div>
            <div className="space-y-3 min-h-[300px] bg-[hsl(var(--muted))]/30 border border-border p-3">
              {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">Nothing here.</div>}
              {items.map((o) => {
                const c = customers.find((x) => x.id === o.customer_id);
                const actions = DESIGNER_ACTIONS[status] || [];
                return (
                  <div key={o.id} className="velvet-card p-4" data-testid={`prod-card-${o.number}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-mono text-[11px] text-muted-foreground">{o.number}</span>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="font-serif text-lg leading-tight mt-1">
                      {c?.name || '—'}
                      {o.is_demo && <span className="ml-2 text-[9px] px-1 py-0.5 border border-border text-muted-foreground uppercase tracking-wider align-middle">DEMO</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{o.occasion || '—'}</div>

                    <div className="mt-3 border border-border p-3 bg-background/60">
                      <div className="velvet-eyebrow flex items-center gap-1 mb-2"><Package className="h-3 w-3" /> Items</div>
                      <ul className="text-sm space-y-1">
                        {(o.order_lines || []).map((li) => (
                          <li key={li.id} className="flex justify-between">
                            <span>{li.item_name}</span>
                            <span className="font-mono text-muted-foreground">×{li.qty}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {o.production_notes && (
                      <div className="mt-3">
                        <div className="velvet-eyebrow flex items-center gap-1 mb-1"><ClipboardList className="h-3 w-3" /> Instructions</div>
                        <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">{o.production_notes}</p>
                      </div>
                    )}

                    {o.blocked_reason && o.status === 'Blocked' && (
                      <div className="mt-3 border border-destructive/40 bg-destructive/10 p-2 text-xs">
                        <div className="uppercase tracking-[0.14em] text-destructive text-[10px] mb-1">Blocked</div>
                        {o.blocked_reason}
                      </div>
                    )}

                    {actions.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {actions.map((a) => (
                          <Button key={a.label} size="sm" className={`rounded-sm ${a.color}`}
                            onClick={() => {
                              if (a.requiresReason) { setBlockFor(o.id); setBlockReason(''); }
                              else move(o.id, a.next);
                            }}
                            data-testid={`action-${o.number}-${a.next.toLowerCase().replace(/\s+/g,'-')}`}>
                            <a.icon className="h-3.5 w-3.5 mr-1" /> {a.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!blockFor} onOpenChange={(v) => !v && setBlockFor(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reason for blocking</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <Label>Why is production blocked?</Label>
            <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="rounded-sm" placeholder="Missing flowers / broken vase / …" autoFocus data-testid="block-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBlockFor(null)}>Cancel</Button>
            <Button disabled={!blockReason.trim()} onClick={async () => { await move(blockFor, 'Blocked', blockReason.trim()); setBlockFor(null); }} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-block-btn">
              Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
